import { useEffect, useRef, useState } from "react";
import { X, Puzzle, KeyRound, Server, Loader2 } from "lucide-react";
import { useLoginActions } from "@/hooks/useLoginActions";
import { validateNsec, validateBunkerUri } from "@/lib/security";

/**
 * Pop-art styled login dialog. Supports browser extension (NIP-07),
 * secret key (nsec), and remote signer (bunker://). Ported and
 * restyled from treasures.to.
 */
export function LoginDialog({
  isOpen,
  onClose,
  onLogin,
}: {
  isOpen: boolean;
  onClose: () => void;
  onLogin: () => void;
}) {
  const login = useLoginActions();
  const [nsec, setNsec] = useState("");
  const [bunker, setBunker] = useState("");
  const [busy, setBusy] = useState<null | "ext" | "nsec" | "bunker">(null);
  const [error, setError] = useState<string | null>(null);
  const hasExtension =
    typeof window !== "undefined" && "nostr" in window;
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setNsec("");
      setBunker("");
      setBusy(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const done = () => {
    onLogin();
    onClose();
  };

  const handleExtension = async () => {
    setError(null);
    setBusy("ext");
    try {
      await login.extension();
      done();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Extension login failed.",
      );
    } finally {
      setBusy(null);
    }
  };

  const handleNsec = () => {
    setError(null);
    if (!validateNsec(nsec)) {
      setError("That doesn't look like a valid nsec.");
      return;
    }
    setBusy("nsec");
    try {
      login.nsec(nsec.trim());
      done();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Key login failed.");
    } finally {
      setBusy(null);
    }
  };

  const handleBunker = async () => {
    setError(null);
    if (!validateBunkerUri(bunker)) {
      setError("Enter a valid bunker:// URI.");
      return;
    }
    setBusy("bunker");
    try {
      await login.bunker(bunker.trim());
      done();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Remote signer login failed.",
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        className="relative z-10 w-full max-w-md rounded-2xl border-4 border-black bg-white p-6 shadow-[8px_8px_0_0_#000]"
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full border-2 border-black bg-[#ffd23f] p-1 hover:scale-110 transition-transform"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <h2
          className="mb-1 text-2xl font-black uppercase tracking-tight"
          style={{ color: "#3d1a5e" }}
        >
          Log in
        </h2>
        <p className="mb-5 text-sm text-gray-600">
          Sign in with Nostr to bind your prints to your npub.
        </p>

        {error && (
          <p className="mb-4 rounded-lg border-2 border-red-500 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
            {error}
          </p>
        )}

        {hasExtension && (
          <button
            onClick={handleExtension}
            disabled={busy !== null}
            className="mb-4 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-black px-4 py-3 text-sm font-black uppercase tracking-wide text-white shadow-[3px_3px_0_0_#000] transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-50"
            style={{
              background: "linear-gradient(135deg, #ff7a1a 0%, #ff3ea5 100%)",
            }}
          >
            {busy === "ext" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Puzzle className="h-4 w-4" />
            )}
            Browser Extension
          </button>
        )}

        {/* nsec */}
        <div className="mb-4">
          <label className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gray-700">
            <KeyRound className="h-3.5 w-3.5" /> Secret key (nsec)
          </label>
          <div className="flex gap-2">
            <input
              type="password"
              value={nsec}
              onChange={(e) => setNsec(e.target.value)}
              placeholder="nsec1..."
              className="flex-1 rounded-lg border-2 border-black px-3 py-2 font-mono text-xs"
            />
            <button
              onClick={handleNsec}
              disabled={busy !== null || !nsec}
              className="rounded-lg border-2 border-black bg-[#3ec1ff] px-3 py-2 text-xs font-black uppercase shadow-[2px_2px_0_0_#000] transition-transform hover:scale-105 active:scale-95 disabled:opacity-50"
            >
              {busy === "nsec" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Go"
              )}
            </button>
          </div>
        </div>

        {/* bunker */}
        <div>
          <label className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gray-700">
            <Server className="h-3.5 w-3.5" /> Remote signer (bunker://)
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={bunker}
              onChange={(e) => setBunker(e.target.value)}
              placeholder="bunker://..."
              className="flex-1 rounded-lg border-2 border-black px-3 py-2 font-mono text-xs"
            />
            <button
              onClick={handleBunker}
              disabled={busy !== null || !bunker}
              className="rounded-lg border-2 border-black bg-[#3ddc97] px-3 py-2 text-xs font-black uppercase shadow-[2px_2px_0_0_#000] transition-transform hover:scale-105 active:scale-95 disabled:opacity-50"
            >
              {busy === "bunker" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Go"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
